import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Skeleton, Paper } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import NavBar from '../../../Components/NavBar/navbar';
import ReplayPlayer from '../../../Components/Arcade/ReplayPlayer';
import ArcadeHubNav, { arcadeHubPath, ARCADE_SECTIONS } from '../ArcadeHubNav';
import { API_ENDPOINTS } from '../../../utils/config';
import { THEME } from '../../../theme';
import usePageTitle from '../../../Components/Arcade/ui/usePageTitle';

const ReplayPage = () => {
    usePageTitle('Replay');
    const { runId } = useParams();
    const navigate = useNavigate();
    const [run, setRun] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const dashboardPath = arcadeHubPath(ARCADE_SECTIONS.dashboard);
    const closeReplay = useCallback(() => {
        navigate(dashboardPath);
    }, [navigate, dashboardPath]);

    useEffect(() => {
        let mounted = true;
        async function fetchRun() {
            setLoading(true);
            setError(null);
            try {
                const res = await fetch(API_ENDPOINTS.RUN_DETAIL(runId));
                if (!res.ok) {
                    throw new Error(res.status === 404 ? 'not-found' : 'failed');
                }
                const data = await res.json();
                if (mounted) setRun(data.run || data.entry || data);
            } catch (e) {
                if (mounted) {
                    setError(e && e.message === 'not-found'
                        ? 'This run no longer exists or the link is wrong.'
                        : 'Check your connection and try again in a moment.');
                }
            }
            if (mounted) setLoading(false);
        }
        if (runId) fetchRun();
        return () => { mounted = false; };
    }, [runId]);

    return (
        <Box sx={{ bgcolor: THEME.bg, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <NavBar />
            <Box sx={{ flexGrow: 1, pt: 12, pb: 8 }}>
                <Container maxWidth="md">
                    <ArcadeHubNav activeSection={ARCADE_SECTIONS.dashboard} />

                    <Button
                        component={Link}
                        to={dashboardPath}
                        startIcon={<ArrowBack />}
                        sx={{ color: THEME.secondary, mb: 3 }}
                    >
                        Back to Dashboard
                    </Button>

                    <Typography variant="h4" component="h1" sx={{ color: 'white', fontWeight: 800, mb: 3, fontSize: { xs: '1.6rem', sm: '2.125rem' } }}>
                        Run replay
                    </Typography>

                    {loading && (
                        <Paper sx={{ p: 3, bgcolor: THEME.cardBg, border: `1px solid ${THEME.border}`, borderRadius: 3 }}>
                            <Skeleton variant="text" width="40%" sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                            <Skeleton variant="rectangular" height={340} sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 2 }} />
                        </Paper>
                    )}

                    {!loading && error && (
                        <Paper sx={{ p: 4, bgcolor: THEME.cardBg, border: `1px solid ${THEME.border}`, borderRadius: 3, textAlign: 'center' }}>
                            <Typography sx={{ color: THEME.accent, fontWeight: 'bold', mb: 1 }}>
                                We couldn't load this replay.
                            </Typography>
                            <Typography variant="body2" sx={{ color: THEME.secondary }}>
                                {error}
                            </Typography>
                        </Paper>
                    )}

                    {!loading && !error && run && (
                        <ReplayPlayer run={run} onClose={closeReplay} />
                    )}
                </Container>
            </Box>
        </Box>
    );
};

export default ReplayPage;
